import { heroDelivery, heroPreloadLinks } from "@/lib/hero-delivery";
import { RichText } from "@/components/site/RichText";
import { LocaleLink, type PublicPath } from "@/components/site/LocaleLink";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { Overline } from "@/components/site/Overline";
import { ServiceBand } from "@/components/site/ServiceBand";
import {
  Thermometer,
  Layers,
  Umbrella,
  BrickWall,
  Leaf,
  ArrowRight,
  Snowflake,
  Sun,
  ShieldCheck,
  Factory,
  Droplets,
  Sparkles,
} from "lucide-react";
import warmtepompenHero from "../../assets/warmtepompen-hero.avif";
import warmtepompenHeroSmall from "../../assets/warmtepompen-hero-640.avif";
import { FaqSection } from "@/components/site/FaqSection";
import { faqs, faqJsonLd } from "@/content/faqs";
import { canonical, breadcrumbJsonLd, serviceJsonLd, shareImageMeta, langPageMeta } from "@/content/site";
import { publishedPageQuery } from "@/lib/content-queries";
import { pageContent } from "@/lib/page-content";
import { usePageContent, validateVoorvertoning } from "@/lib/use-page-content";
import { PhotoBlocks } from "@/components/site/PhotoBlocks";

const PATH = "/warmtepompen-afdekkingen";
const SLEUTEL = "warmtepompen-afdekkingen";

const HERO_FALLBACK = { src: warmtepompenHero, srcSet: `${warmtepompenHeroSmall} 640w, ${warmtepompenHero} 1024w` };

const CODE_META_TITLE = "Warmtepomp, rolluik & overkapping zwembad | Limburg";
const CODE_META_DESCRIPTION =
  "Warmtepompen, OCEA zwembadrolluiken met solar lamellen, AURA overkappingen en boordstenen. Plaatsing en onderhoud in Limburg en Zuid-Nederland.";

export const Route = createFileRoute("/$lang/warmtepompen-afdekkingen")({
  staticData: { sitemap: true },
  validateSearch: validateVoorvertoning,
  loader: async ({ context, params }) => ({
    page: await context.queryClient.ensureQueryData(publishedPageQuery(SLEUTEL, params.lang)),
  }),
  head: ({ params, loaderData }) => {
    const c = pageContent(loaderData?.page);
    const title = c.metaTitle(CODE_META_TITLE);
    const description = c.metaDescription(CODE_META_DESCRIPTION);
    const heroPhoto = heroDelivery(c.section("hero"), HERO_FALLBACK);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: "Warmtepompen, OCEA rolluiken, AURA overkappingen en boordstenen voor zwembaden." },
        { property: "og:type", content: "website" },
        ...langPageMeta(params.lang, PATH).meta,
        { name: "twitter:card", content: "summary_large_image" },
        ...shareImageMeta("warmtepompen", "Zwembadwarmtepomp en OCEA rolluikafdekking"),
      ],
      links: [...langPageMeta(params.lang, PATH).links, ...heroPreloadLinks(heroPhoto)],
      scripts: [
        faqJsonLd(faqs.warmtepompen),
        breadcrumbJsonLd("Warmtepompen & afdekkingen", PATH),
        serviceJsonLd({
          name: "Zwembadverwarming en afdekkingen",
          serviceType: "Zwembadverwarming en zwembadafdekking",
          description:
            "Plaatsing van lucht-water warmtepompen, OCEA zwembadrolluiken met PVC of polycarbonaat lamellen, AURA zwembadoverkappingen en boordstenen.",
          path: PATH,
        }),
      ],
    };
  },
  component: WarmtepompenPage,
});

const productIcons = [Thermometer, Layers, Umbrella, BrickWall, Snowflake, Sun];

const products = [
  {
    titel: "Warmtepompen",
    tekst:
      "Moderne lucht-water warmtepompen die uw zwemwater op een energiezuinige manier verwarmen. Bespaar tot 80% op verwarmingskosten.",
  },
  {
    titel: "Rolluiken",
    tekst:
      "Inbouw- en opbouwrolluiken met PVC of polycarbonaat lamellen. Wij werken met het Belgische merk OCEA voor veilige, duurzame en onderhoudsvriendelijke oplossingen.",
  },
  {
    titel: "Overkappingen",
    tekst:
      "Zwembadoverkappingen van het Oostenrijks topmerk AURA. Verleng het zwembadseizoen en bescherm uw bad tegen weer en wind.",
  },
  {
    titel: "Boordstenen",
    tekst: "Natuursteen, keramiek en beton in verschillende afwerkingen. De perfecte rand rondom uw zwembad.",
  },
  {
    titel: "Winteronderhoud",
    tekst:
      "Winterklaar maken van uw zwembad, leidingwerk en techniek. Zo start u volgend seizoen zonder verrassingen.",
  },
  {
    titel: "Zomeronderhoud",
    tekst: "Opstarten, wateranalyse, filterreiniging en regelmatige nazorg. Wij houden uw water helder en gezond.",
  },
];

const strengthIcons = [ShieldCheck, Factory, Droplets, Sparkles];

const oceaStrengths = [
  {
    titel: "Veiligheid voor uw kinderen",
    tekst:
      "Het unieke lamellenprofiel geeft extra drijfvermogen en draagt het gewicht van een volwassene. Getest volgens de norm NF P90-308, zowel bovengronds als onder water.",
  },
  {
    titel: "100% eigen productie in België",
    tekst:
      "Extrusie van de lamellen, RVS-structuren en aandrijving worden intern gemaakt. Korte levertermijnen en een constante, controleerbare kwaliteit.",
  },
  {
    titel: "Minder verdamping, minder kosten",
    tekst:
      "Een gesloten rolluik beperkt de verdamping van water en behandelingsproducten sterk. U verbruikt minder energie én minder chemie.",
  },
  {
    titel: "Anti-algen uv+-profiel",
    tekst:
      "Het uv+-profiel blokkeert lichtinval in het scharnier, waardoor algvorming tot een minimum beperkt blijft. De extra anti-uv laag verlengt de levensduur.",
  },
];

const oceaSystems = [
  {
    titel: "Rolluikbak achteraan",
    tekst:
      "Het rolluik wordt opgeborgen in een gekoppelde bak achter het zwembad, met RVS-liggers of uitkragende liggers voor een hoger waterpeil en zwaardere afwerking.",
  },
  {
    titel: "Nis in de zwembadwand",
    tekst:
      "Onzichtbaar weggewerkt achter een scheidingswand: de Pro-nis in RVS met af te werken panelen, of de lichtere Light-nis voor liner-baden.",
  },
  {
    titel: "Onder een luik of trap",
    tekst:
      "Verborgen onder een luik in de bodem, in de eerste trede of onder een licht hellend vlak. Voor vrije vormen bestaat er zelfs een gemotoriseerd luik.",
  },
  {
    titel: "In de plage of zitbank",
    tekst:
      "Het rolluik verdwijnt in de bank aan de rand van het bad — precies zoals bij onze realisatie van 10 x 3 m met solar lamellen.",
  },
  {
    titel: "Bovengronds (Edge)",
    tekst:
      "Edge Simple of Edge Bench met RVS-consoles en aluminium profielen in wit of grijs, optioneel bekleed met hout. Ideaal bij renovatie.",
  },
];

function WarmtepompenPage() {
  const { lang } = Route.useParams();
  const { voorvertoning } = Route.useSearch();
  const c = usePageContent(SLEUTEL, lang, voorvertoning);
  const hero = c.section("hero");
  const heroPhoto = heroDelivery(hero, HERO_FALLBACK);

  return (
    <div className="flex flex-col">
      <PageHero
        title={c.veld("hero", "titel", "Warmtepompen & afdekkingen")}
        subtitle={c.rijkInline("hero",
          "Verleng het zwembadseizoen en verlaag het onderhoud met slimme verwarming, OCEA rolluiken, AURA overkappingen en kwalitatieve boordafwerking.",
        )}
        imageSrc={heroPhoto.src}
        imageFocal={heroPhoto.focal}
        imageSrcSet={heroPhoto.srcSet}
        imageAlt={c.veld("hero", "alt", "Zwembad met warmtepomp, rolluik en overkapping")}
        cta={{
          label: c.veld("hero", "knopTekst", "Advies op maat"),
          to: c.veld("hero", "knopLink", "/offerte") as PublicPath,
        }}
        secondaryCta={{
          label: c.veld("hero", "knop2Tekst", "Bekijk realisaties"),
          to: c.veld("hero", "knop2Link", "/realisaties") as PublicPath,
        }}
      />

      <Section tone="light">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Overline>{c.veld("verwarming", "overline", "Verwarming")}</Overline>
            <h2 className="mt-4 display-section">
              {c.veld("verwarming", "titel", "Hoe verwarm ik mijn zwembad het voordeligst?")}
            </h2>
            {c
              .alineas("verwarming", [
                "De combinatie van een lucht-water warmtepomp met een afdekking is veruit het voordeligst. De warmtepomp haalt warmte uit de buitenlucht en levert daarmee een veelvoud van de stroom die ze verbruikt; de afdekking houdt die warmte 's nachts binnen. Zonder afdekking verdwijnt het grootste deel van de warmte via verdamping aan het wateroppervlak.",
                "Solarlamellen van OCEA verwarmen het water bovendien zelf 4 tot 8 °C door zonnewarmte op te vangen. In de praktijk betekent dat: uw warmtepomp moet minder vaak draaien, u verbruikt minder stroom en minder chemie, en u zwemt van het vroege voorjaar tot in het najaar. Wij dimensioneren de warmtepomp altijd op de inhoud van uw bad, de gewenste temperatuur en het type afdekking — een te kleine pomp draait constant, een te grote is verloren investering.",
              ])
              .map((alinea, index) => (
                <p
                  key={alinea.slice(0, 24)}
                  className={`${index === 0 ? "mt-6" : "mt-4"} max-w-[70ch] text-[17px] leading-7 text-on-surface-variant`}
                >
                  <RichText waarde={alinea} inline />
                </p>
              ))}
          </div>
          <div>
            <Overline>{c.veld("afdekking-keuze", "overline", "Afdekking kiezen")}</Overline>
            <h2 className="mt-4 display-section">{c.veld("afdekking-keuze", "titel", "Rolluik of overkapping?")}</h2>
            <p className="mt-6 max-w-[70ch] text-[17px] leading-7 text-on-surface-variant">
              {c.rijkInline("afdekking-keuze", "Beide beschermen uw bad, maar ze doen dat anders. Kort samengevat:")}
            </p>
            <ul className="mt-6 divide-y divide-border border-y border-border text-on-surface-variant">
              <li className="py-4 text-sm leading-6">
                <strong className="text-foreground">Rolluik (OCEA):</strong> discreet, beperkt warmteverlies en
                verdamping, houdt bladeren buiten en biedt veiligheid voor kinderen — het lamellenprofiel is getest
                volgens NF P90-308. In te bouwen in de plage, onder een luik of trap, in een nis of bovengronds.
              </li>
              <li className="py-4 text-sm leading-6">
                <strong className="text-foreground">Overkapping (AURA):</strong> verwarmt het water en de ruimte
                errond, laat u ook bij wind en regen zwemmen en verlengt het seizoen het sterkst. Neemt wel zichtbaar
                plaats in de tuin in.
              </li>
              <li className="py-4 text-sm leading-6">
                <strong className="text-foreground">Samen:</strong> veel klanten kiezen een rolluik voor dagelijks
                gemak en veiligheid, en een overkapping wanneer ze echt jaarrond willen zwemmen.
              </li>
            </ul>
          </div>
        </div>
      </Section>

      <Section tone="mist">
        <div className="max-w-[70ch]">
          <Overline>{c.veld("aanbod", "overline", "Aanbod")}</Overline>
          <h2 className="mt-4 display-section">{c.veld("aanbod", "titel", "Productcategorieën")}</h2>
          <p className="mt-6 text-[17px] leading-7 text-on-surface-variant">
            {c.rijkInline("aanbod",
              "Wij adviseren, leveren en installeren alle technische en esthetische aanvullingen voor uw zwembad.",
            )}
          </p>
        </div>
        <div className="mt-12 grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3">
          {c.kaarten("aanbod", products).map((product, index) => {
            const Icon = productIcons[index % productIcons.length]!;
            return (
              <article key={product.titel} className="bg-background p-6">
                <Icon className="h-11 w-11 text-brand-deep-water" strokeWidth={1.75} />
                <h3 className="mt-4 font-display text-lg font-semibold">{product.titel}</h3>
                <p className="mt-2 text-sm leading-6 text-on-surface-variant">{product.tekst}</p>
              </article>
            );
          })}
        </div>
      </Section>

      <ServiceBand
        items={c.kaarten("ocea-sterktes", oceaStrengths).map((kaart, index) => ({
          icon: strengthIcons[index % strengthIcons.length]!,
          title: kaart.titel,
          text: kaart.tekst,
        }))}
      />

      <Section tone="light">
        <div className="max-w-[70ch]">
          <Overline>{c.veld("ocea", "overline", "OCEA — Belgische fabrikant")}</Overline>
          <h2 className="mt-4 display-section">{c.veld("ocea", "titel", "Rolluiken die je een leven lang meegaan")}</h2>
          <p className="mt-6 text-[17px] leading-7 text-on-surface-variant">
            {c.rijkInline("ocea",
              "OCEA International ontwerpt en produceert al meer dan 20 jaar zwembadafdekkingen in België. Van de extrusie van de lamellen tot de RVS-structuren en de aandrijving: alles wordt in eigen huis gemaakt. Dat betekent korte levertermijnen, oplossingen op maat en een kwaliteit die wij met een gerust hart bij onze klanten plaatsen.",
            )}
          </p>
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <h3 className="font-display text-xl font-semibold">
              {c.veld("systemen", "titel", "Een systeem voor elk zwembad")}
            </h3>
            <p className="mt-4 text-[17px] leading-7 text-on-surface-variant">
              {c.rijkInline("systemen",
                "Zowel bij nieuwbouw als renovatie plaatsen wij het passende OCEA-systeem. De motor kan onder water zitten in een motorput (Classic) of als interne buismotor in de as (Logic), telkens in verschillende vermogens.",
              )}
            </p>
            <ul className="mt-8 divide-y divide-border border-y border-border">
              {c.kaarten("systemen", oceaSystems).map((sys) => (
                <li key={sys.titel} className="py-4">
                  <p className="font-display text-base font-semibold">{sys.titel}</p>
                  <p className="mt-1 text-sm leading-6 text-on-surface-variant">{sys.tekst}</p>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-display text-xl font-semibold">{c.veld("lamellen", "titel", "Lamellen en kleuren")}</h3>
            <p className="mt-4 text-[17px] leading-7 text-on-surface-variant">
              {c.rijkInline("lamellen",
                "Kies uit PVC- en polycarbonaat lamellen in een zeer ruim kleurengamma. Elke hoogwaardige lat heeft een anti-uv toplaag en het unieke uv+-profiel dat algvorming tegengaat.",
              )}
            </p>
            <ul className="mt-8 divide-y divide-border border-y border-border text-on-surface-variant">
              <li className="py-4 text-sm leading-6">
                <strong className="text-foreground">Solarlamellen</strong> — blauw, transparant, cobalt, platinium, granite,
                sandstone, olive, gold, lagoon of ocean. De zwarte onderlaag vangt de zonnewarmte op.
              </li>
              <li className="py-4 text-sm leading-6">
                <strong className="text-foreground">Doorzichtige lamellen</strong> — Crystal Clear en Transparent Blue laten de
                zwembadverlichting mooi tot zijn recht komen.
              </li>
              <li className="py-4 text-sm leading-6">
                <strong className="text-foreground">PVC-klassiekers</strong> — wit, grijs, zand en mokka: sober, betaalbaar en
                tijdloos.
              </li>
              <li className="py-4 text-sm leading-6">
                <strong className="text-foreground">Condensatiemasker</strong> — donkere en metallic tinten verbergen de
                condensatie in de lat, zodat het rolluik jarenlang strak blijft ogen.
              </li>
            </ul>
            <div className="mt-8 bg-primary p-6 text-primary-foreground">
              <Overline onDark>{c.veld("weetje", "overline", "Weetje")}</Overline>
              <p className="mt-3 text-sm leading-6 text-primary-foreground/80">
                {c.rijkInline("weetje",
                  "Solarlamellen kunnen uw zwemwater 4 tot 8 °C opwarmen. In combinatie met een warmtepomp betaalt een rolluik zichzelf snel terug.",
                )}
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section tone="mist">
        <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="bg-primary p-8 text-primary-foreground lg:p-10">
            <Leaf className="h-11 w-11 text-secondary" strokeWidth={1.75} />
            <h3 className="mt-5 font-display text-xl font-semibold">
              {c.veld("energie", "titel", "Energiebesparing op de lange termijn")}
            </h3>
            <p className="mt-4 text-[17px] leading-7 text-primary-foreground/80">
              {c.rijkInline("energie",
                "Een warmtepomp in combinatie met een goede afdekking kan uw verwarmingskosten fors verlagen. Wij rekenen voor u vooraf de terugverdientijd uit.",
              )}
            </p>
          </div>
          <div>
            <Overline>{c.veld("partners", "overline", "Partners")}</Overline>
            <h2 className="mt-4 display-section">{c.veld("partners", "titel", "Onze merken")}</h2>
            <p className="mt-6 max-w-[70ch] text-[17px] leading-7 text-on-surface-variant">
              {c.rijkInline("partners",
                "Wij werken samen met gerenommeerde fabrikanten voor afdekkingen en overkappingen:",
              )}
            </p>
            <ul className="mt-8 divide-y divide-border border-y border-border text-on-surface-variant">
              <li className="py-4 text-sm leading-6">
                <strong className="text-foreground">OCEA</strong> — Belgische fabrikant van inbouw- en opbouwrolluiken met PVC of polycarbonaat lamellen.{" "}
                <a href="https://www.ocea.be" target="_blank" rel="noopener noreferrer" className="text-brand-deep-water underline underline-offset-4">www.ocea.be</a>
              </li>
              <li className="py-4 text-sm leading-6">
                <strong className="text-foreground">AURA</strong> — Oostenrijks topmerk voor zwembadoverkappingen.{" "}
                <a href="https://www.aura.co.at/" target="_blank" rel="noopener noreferrer" className="text-brand-deep-water underline underline-offset-4">www.aura.co.at</a>
              </li>
            </ul>
            <Button asChild className="mt-8">
              <LocaleLink to={c.veld("partners", "knopLink", "/offerte") as PublicPath}>
                {c.veld("partners", "knopTekst", "Gratis adviesgesprek")} <ArrowRight className="ml-2 h-4 w-4" />
              </LocaleLink>
            </Button>
          </div>
        </div>
      </Section>
      <PhotoBlocks content={c} />
      <FaqSection items={faqs.warmtepompen} />
    </div>
  );
}
